import { CognitoUser } from 'amazon-cognito-identity-js';
import { Auth } from 'aws-amplify';
import { FormikHelpers, FormikProps } from 'formik';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import UsersClient from 'src/clients/UsersClient';
import {
  INVALID_CODE,
  MFA_INPUT_DESCRIPTION,
  MFA_INPUT_TITLE,
  REDIRECT_PATH_AFTER_GOOGLE_LOGIN,
  SESSION_EXPIRY_ERR_MSG,
} from 'src/constants/authConsts';
import { PortalConfigContext, RouteContext } from 'src/context';
import { OnboardingFormCard } from 'src/legacy/components/Onboarding/OnboardingFormCard';
import {
  OnboardingLoginChallengeFormValues,
  OnboardingLoginForm,
} from 'src/legacy/components/Onboarding/OnboardingForms';
import {
  OnboardingLoginChallengeForm,
  OnboardingLoginChallengeFormProps,
} from 'src/legacy/components/Onboarding/OnboardingForms/OnboardingLoginChallengeForm';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import { BrandAvatar } from 'src/legacy/components/UI/Avatars/BrandAvatar';
import {
  getIdentityProvider,
  GetSignInMetadata,
  GetSignInMetadataInput,
} from 'src/utils/AuthUtils';
import { ensureApiError } from 'src/utils/Errors';
import { removeSpaceToLowerCase } from 'src/utils/StringUtils';

type ChallengeParamType = CustomChallengeParams | MfaChallengeParams;

export const OnboardingAuthContentWrapper = ({
  challengeParam,
  children,
}: React.PropsWithChildren<{
  challengeParam?: ChallengeParamType;
}>) => {
  const { t } = useTranslation();
  return (
    <OnboardingFormCard>
      <BrandAvatar />
      <div className="mt-3 text-center px-4 sm:px-0">
        <BaseTypography fontType="18Medium">
          {challengeParam && challengeParam.type === 'SOFTWARE_TOKEN_MFA'
            ? MFA_INPUT_TITLE
            : t(($) => $.page_onboarding.login.title)}
        </BaseTypography>
      </div>
      {challengeParam && challengeParam.type === 'SOFTWARE_TOKEN_MFA' && (
        <div className="text-center px-4 sm:px-0">
          <BaseTypography fontType="13Medium">
            {MFA_INPUT_DESCRIPTION}
          </BaseTypography>
        </div>
      )}
      <div className="mb-0 mt-10">{children}</div>
    </OnboardingFormCard>
  );
};

interface queryParams {
  email?: string;
  auth?: string;
}

interface CustomChallengeParams {
  email: string;
  preferred_username: string;
  sub: string;
  USERNAME: string;
  type: 'CUSTOM_CHALLENGE';
}

interface MfaChallengeParams {
  type: 'SOFTWARE_TOKEN_MFA';
  friendlyDeviceName?: string;
  userId?: string;
  email: string;
}

interface OnboardingAuthProps {
  onAuthComplete: () => void;
  user?: CognitoUser;
  mfaChallengeParam?: MfaChallengeParams;
}

export const OnboardingAuth: React.FC<OnboardingAuthProps> = ({
  onAuthComplete,
  user,
  mfaChallengeParam,
}) => {
  // store sign in user to be used in challenge response. Use ref to store the instance
  // of cognito user to submit custom auth challenge.
  const signInUserRef = React.useRef<CognitoUser | undefined>(user);
  // store challenge params in sign-in response to be used in challenge response
  const [challengeParam, setChallengeParam] = React.useState<
    ChallengeParamType | undefined
  >(mfaChallengeParam);

  // check email/password query params to auto-fill login form
  const { query }: { query: queryParams } = React.useContext(RouteContext);
  const portalConfig = React.useContext(PortalConfigContext);
  const useIdentityProviders = useFeatureFlagEnabled('use-identity-providers');

  const onboardingFormRef =
    React.useRef<FormikHelpers<OnboardingLoginChallengeFormProps>>(null);

  const loginChallengeFormRef =
    React.useRef<FormikProps<OnboardingLoginChallengeFormValues>>(null);

  /**
   * Handle the completed sign-in process. This will set the cookie and local storage
   * for the user so that it can be used in the next steps of the login flow (e.g. workspace select)
   * @param user user returned in sign-in response
   * @param userEmail email as typed by user
   */
  const completeSignIn = async (cognitoUser: any, userEmail: string) => {
    // The sign-in method should use the email as typed by user
    // to handle different login settings (backward compatibility, with existing pools)
    // but we need to use the email in lowercase without spaces to set the cookie and workspaces
    const email = removeSpaceToLowerCase(userEmail);
    await UsersClient.setAppUserCookie(
      cognitoUser,
      'workspace_login',
      'workspaces',
      email,
    );
    window.localStorage.setItem(
      `CognitoIdentityServiceProvider.${portalConfig.AWS.Auth.userPoolWebClientId}.LastAuthUser`,
      cognitoUser.username,
    );

    // The flag we set here is used to ensure that analytics for this user session aren't stored
    // since any actions taken by a Copilot user should not skew the data for the workspace.
    if (window.location.pathname.includes('/admin')) {
      window.localStorage.setItem(`proxy#${cognitoUser.username}`, 'true');
    }

    onAuthComplete();
  };

  /**
   * Handle the submit event from the login form. This will start the login process which
   * will either return a challenge response or a successful login response. When there is
   * a challenge response, we keep track of the sign in user in ref to later submit the challenge code.
   * Once the challenge is completed, this method is called again with the challenge response.
   * @param submitValues values from the login form
   */
  const handleSubmitDone = async (
    submitValues: OnboardingLoginChallengeFormValues,
    options?: {
      useIdentityProviders: boolean;
    },
  ) => {
    const { email: inputEmail, password } = submitValues;
    try {
      if (
        signInUserRef.current &&
        challengeParam &&
        submitValues.challengeResponse
      ) {
        let cognitoUser;
        if (challengeParam.type === 'SOFTWARE_TOKEN_MFA') {
          cognitoUser = await Auth.confirmSignIn(
            signInUserRef.current,
            submitValues.challengeResponse,
            challengeParam.type,
          );
        } else {
          // when there is a challenge response, we need to complete the login process
          // by sending the challenge response
          cognitoUser = await Auth.sendCustomChallengeAnswer(
            signInUserRef.current,
            submitValues.challengeResponse,
          );
        }

        completeSignIn(cognitoUser, inputEmail);
      } else {
        // Check if the email domain uses an identity provider (SSO)
        const identityProvider = getIdentityProvider(inputEmail);
        if (options?.useIdentityProviders && identityProvider) {
          // Save the redirect path before federated sign-in (similar to Google auth flow)
          const urlParams = new URLSearchParams(window.location.search);
          const next = urlParams.get('next');
          if (next) {
            window.sessionStorage.setItem(
              REDIRECT_PATH_AFTER_GOOGLE_LOGIN,
              next,
            );
          }

          // For IDP domains, use federated sign-in which will redirect to the SSO provider
          await Auth.federatedSignIn({ customProvider: identityProvider });
          // The user will be redirected, so we don't need to do anything else here
          return false;
        }

        // when there is no challenge response, we need to start the login process
        const cognitoUser = await Auth.signIn(
          inputEmail,
          password,
          GetSignInMetadata(query as GetSignInMetadataInput),
        );

        console.info('start sign-in result', cognitoUser);
        // check if custom_auth challenge is required show input for code
        if (cognitoUser.challengeName === 'CUSTOM_CHALLENGE') {
          // the CUSTOM_CHALLENGE case occurs when we login through the /admin route to proxy in as an existing user
          signInUserRef.current = cognitoUser;
          setChallengeParam(cognitoUser.challengeParam);
          return false;
        }
        if (cognitoUser.challengeName === 'SOFTWARE_TOKEN_MFA') {
          // the SOFTWARE_TOKEN_MFA case occurs when we login as a user with MFA enabled
          signInUserRef.current = cognitoUser;
          setChallengeParam({
            type: cognitoUser.challengeName,
            email: inputEmail,
            ...cognitoUser.challengeParam,
          });
          return false;
        }

        completeSignIn(cognitoUser, inputEmail);
      }
    } catch (err) {
      const e = ensureApiError(err);
      let { message } = e;
      try {
        if (message.includes('UserMigration failed')) {
          message = 'Invalid email or password';
        } else if (message.includes('session is expired')) {
          // expiry can happen when a login challenge is found
          // and not answered within 3 minutes
          message = SESSION_EXPIRY_ERR_MSG;
          loginChallengeFormRef.current?.setFieldError(
            'challengeResponse',
            message,
          );
          return;
        } else if (e.code === 'CodeMismatchException') {
          message = INVALID_CODE;
          loginChallengeFormRef.current?.setFieldError(
            'challengeResponse',
            message,
          );
          return;
        }
      } catch (ex) {
        console.error(ex, 'error parsing error message');
      }
      onboardingFormRef.current?.setFieldError('password', message);
    }

    // return false to skip track event in login form that is meant for portal onboarding
    return false;
  };

  return (
    <OnboardingAuthContentWrapper challengeParam={challengeParam}>
      {challengeParam ? (
        <OnboardingLoginChallengeForm
          initialValues={{
            email: challengeParam.email,
            password: '',
            challengeResponse: '',
          }}
          ref={loginChallengeFormRef}
          handleSubmitDone={handleSubmitDone}
          challengeName={challengeParam.type}
          onAnalyticsEventTracked={() => {}}
          trackedEvents={{}}
        />
      ) : (
        <OnboardingLoginForm
          ref={onboardingFormRef}
          initialValues={{
            email: query.email || '',
            password: query.auth || '',
          }}
          handleSubmitDone={handleSubmitDone}
          showOnboardingLoginActions
          onAnalyticsEventTracked={() => {}}
          trackedEvents={{}}
          useIdentityProviders={useIdentityProviders}
        />
      )}
    </OnboardingAuthContentWrapper>
  );
};
